import {
  areObjectsEqual,
  getCutoffDate,
  getFilterFromQuery,
  getSortAndPaginationFromQuery,
} from 'src/services/app/util.service'
import { TABLE_COL_NAMES } from 'src/services/consts/table-cell.const'
import {
  TICKET_FILTER_FIELDS,
  TICKET_SORT_PAGINATION_FIELDS,
  TICKET_STATUS,
  TICKET_STATUS_CONFIG,
  DUPLICATE_TICKET_CONFIG,
  DIRECTORIES,
  OPTION_QUESTION_TYPES,
  QUESTION_TYPES,
  DEFAULT_FILTER_SORT,
  DRAFTS_SORT_FIELDS,
} from 'src/services/consts/ticket.const'
import {
  TICKET_CREATION_STEPS,
  TICKET_CUSTOM_ID_CUSTOM,
  TICKET_CUSTOM_IDS,
} from 'src/services/consts/ticket-creation.const'
import { refactorMessages } from 'src/services/helpers/message.helper'

export function filterTickets(tickets, filterBy) {
  const ticketsToFilter = [...tickets]

  const searchTerm = filterBy.search ? filterBy.search.toLowerCase() : null
  const cutoffDate = getCutoffDate(filterBy.dateRange)
  const status = filterBy.status ?? DIRECTORIES.inReview

  return ticketsToFilter.filter((ticket) => {
    // Search filter condition
    const matchesSearch =
      !searchTerm ||
      ticket.companyName?.toLowerCase().includes(searchTerm) ||
      ticket.productName?.toLowerCase().includes(searchTerm)

    // Status filter condition
    let matchesStatus = true
    if (status) {
      switch (status) {
        case 'in-review':
          matchesStatus = isTicketUnderReview(ticket.ticketStatus)
          break
        case 'approved':
          matchesStatus = ticket.ticketStatus === TICKET_STATUS.CLOSED && ticket.approved === true
          break
        case 'denied':
          matchesStatus = ticket.ticketStatus === TICKET_STATUS.CLOSED && ticket.approved === false
          break
        case 'drafts':
          matchesStatus = ticket.ticketStatus === TICKET_STATUS.OPENED
          break
        default:
          matchesStatus = true
      }
    }

    // Date range condition
    const matchesDateRange = !cutoffDate || new Date(ticket.dateCreated) >= cutoffDate

    // Return true only if all conditions match
    return matchesSearch && matchesStatus && matchesDateRange
  })
}

export function getDefaultFilter() {
  return {
    ...TICKET_FILTER_FIELDS,
  }
}

export function getDefaultSort() {
  return {
    ...TICKET_SORT_PAGINATION_FIELDS,
  }
}

export function getTicketSortAndPaginationFromQuery(query, options) {
  const tableState = getSortAndPaginationFromQuery(getDefaultSort(), query, options)
  const { status } = getFilterFromQuery(getDefaultFilter(), query)

  // Drafts have no Submitted on column, so the default sort cannot apply.
  if (status === DIRECTORIES.drafts && tableState.sortBy === TABLE_COL_NAMES.DATE_SUBMITTED) {
    return {
      ...tableState,
      ...DRAFTS_SORT_FIELDS,
    }
  }

  return tableState
}

export function getTicketQueryDefaults({ status, sortBy, descending }) {
  const isDraftsFallback =
    status === DIRECTORIES.drafts &&
    sortBy === DRAFTS_SORT_FIELDS.sortBy &&
    descending === DRAFTS_SORT_FIELDS.descending
  return isDraftsFallback ? { ...DEFAULT_FILTER_SORT, ...DRAFTS_SORT_FIELDS } : DEFAULT_FILTER_SORT
}

export function getDirectoryCounts(tickets) {
  // Single pass through all tickets to count by directory
  return tickets.reduce(
    (counts, ticket) => {
      if (ticket.ticketStatus === TICKET_STATUS.OPENED) {
        counts[DIRECTORIES.drafts]++
      } else if (
        ticket.ticketStatus === TICKET_STATUS.PROCESSING ||
        ticket.ticketStatus === TICKET_STATUS.PREPROCESS
      ) {
        counts[DIRECTORIES.inReview]++
      } else if (ticket.ticketStatus === TICKET_STATUS.CLOSED && ticket.approved === true) {
        counts[DIRECTORIES.approved]++
      } else if (ticket.ticketStatus === TICKET_STATUS.CLOSED && ticket.approved === false) {
        counts[DIRECTORIES.denied]++
      }
      return counts
    },
    {
      [DIRECTORIES.inReview]: 0,
      [DIRECTORIES.approved]: 0,
      [DIRECTORIES.denied]: 0,
      [DIRECTORIES.drafts]: 0,
    }
  )
}

export function refactorTicket(rawTicket) {
  // Handle both old structure (ticket_info) and new structure (direct fields)
  const ticketData = rawTicket.ticket_info || rawTicket

  const companyNfInfo = ticketData.company_nf_info || {}
  const productNfInfo = ticketData.product_nf_info || {}

  // Directory enrichment wins when the id resolves; these names are only the fallback.
  const companyName = companyNfInfo.company_nf_name || ticketData.company_name
  const productName = productNfInfo.product_nf_name || ticketData.product_name
  const companyWebsite = companyNfInfo.company_nf_website || ticketData.company_website || ''
  const productWebsite = productNfInfo.product_nf_website || ticketData.product_website || ''
  const companyId = idForCustomEntry(ticketData.company_id, companyNfInfo)
  const productId = idForCustomEntry(ticketData.product_id, productNfInfo)

  const rawQuestionsAnswers = ticketData.questions_answers

  return {
    ticketId: ticketData.ticket_id,
    approved: ticketData.approved,
    approvalId: ticketData.auth_id,

    companyId,
    companyFound: ticketData.company_found,
    companySource: ticketData.company_source,
    companyName,
    companyWebsite,

    dateCreated: ticketData.date_created,
    dateSubmitted: ticketData.date_submitted,
    lastUserUpdate: ticketData.last_user_update,

    productFound: ticketData.product_found,
    productSource: ticketData.product_source,
    productId,
    productName,
    productWebsite,

    teamUse: ticketData.team_use,
    ticketApprover: ticketData.ticket_approver,
    ticketStatus: ticketData.ticket_status,

    userId: ticketData.user_id,
    answers: extractAnswers(rawQuestionsAnswers),
    questionsAnswers: buildQuestionsAnswersSnapshot(rawQuestionsAnswers),
    denyReason: ticketData.deny_reason,
    approvalConditions: ticketData.auth_conditions,
    userMessages: refactorMessages(rawTicket.user_messages),
    uploadedFiles: refactorUploadedFiles(ticketData?.uploaded_files),
  }
}

export function refactorUploadedFiles(uploadedFiles) {
  // Convert to array for easier sorting
  if (!uploadedFiles) return []
  const filesArray = Object.keys(uploadedFiles).map((fileId) => {
    const file = uploadedFiles[fileId]
    const refactored = {
      fileId: fileId,
      contentType: file.content_type,
      filename: file.filename,
      uploadTime: file.upload_time,
      uploadedBy: file.uploaded_by,
    }
    if (typeof file.question_id === 'string' && file.question_id !== '') {
      refactored.questionId = file.question_id
    }
    return refactored
  })
  // Sort by uploadTime descending (newest first) if multiple files
  if (filesArray.length > 1) {
    filesArray.sort((a, b) => {
      const timeA = new Date(a.uploadTime || 0).getTime()
      const timeB = new Date(b.uploadTime || 0).getTime()
      return timeB - timeA
    })
  }
  return filesArray
}

function isFileUploadAnswer(value) {
  return Array.isArray(value) && value.every((item) => typeof item === 'string')
}

/** File ids from a string[]. Anything else is []. */
function normalizeFileUploadAnswer(raw) {
  if (!isFileUploadAnswer(raw)) return []
  return raw.filter((id) => id !== '')
}

function fileIdsAfterChange(raw, fileId, shouldAdd) {
  const ids = normalizeFileUploadAnswer(raw)
  if (!shouldAdd) return ids.filter((id) => id !== fileId)
  if (ids.includes(fileId)) return ids
  return [...ids, fileId]
}

/** Answers map with file-upload values removed, so they are not sent on ticket update. */
export function omitFileUploadAnswers(answers) {
  if (!answers || typeof answers !== 'object') return {}
  const result = {}
  Object.keys(answers).forEach((questionId) => {
    if (!isFileUploadAnswer(answers[questionId])) {
      result[questionId] = answers[questionId]
    }
  })
  return result
}

/**
 * Link `fileId` to `questionId`, or remove it from every file answer when `questionId` is omitted.
 * Writes the flat answers map and the submitted snapshot in one pass.
 */
export function patchTicketFileAnswers({ answers, questionsAnswers, fileId, questionId } = {}) {
  const shouldLink = typeof questionId === 'string' && questionId !== ''
  if (typeof fileId !== 'string' || fileId === '') {
    return { answers, questionsAnswers }
  }

  let nextAnswers = answers
  if (shouldLink) {
    const source = answers && typeof answers === 'object' ? answers : {}
    nextAnswers = {
      ...source,
      [questionId]: fileIdsAfterChange(source[questionId], fileId, true),
    }
  } else if (answers && typeof answers === 'object') {
    nextAnswers = {}
    Object.keys(answers).forEach((id) => {
      const value = answers[id]
      nextAnswers[id] = isFileUploadAnswer(value) ? fileIdsAfterChange(value, fileId, false) : value
    })
  }

  const nextSnapshot = Array.isArray(questionsAnswers)
    ? questionsAnswers.map((entry) => {
        if (!entry) return entry
        const shouldUpdate = shouldLink
          ? entry.questionId === questionId
          : isFileUploadAnswer(entry.answer)
        if (!shouldUpdate) return entry
        return { ...entry, answer: fileIdsAfterChange(entry.answer, fileId, shouldLink) }
      })
    : questionsAnswers

  return { answers: nextAnswers, questionsAnswers: nextSnapshot }
}

// Combined status that merges ticketStatus and approved
export function getCombinedTicketStatus(ticket) {
  const { ticketStatus, approved } = ticket
  if (isTicketUnderReview(ticketStatus)) {
    return TICKET_STATUS_CONFIG.processing
  }

  if (ticketStatus === TICKET_STATUS.CLOSED) {
    if (approved === true) {
      return TICKET_STATUS_CONFIG.approved
    } else if (approved === false) {
      return TICKET_STATUS_CONFIG.denied
    }
  }

  return TICKET_STATUS_CONFIG.draft
}

// Helper to check if ticket is under review (preprocess or processing)
export function isTicketUnderReview(ticketStatus) {
  return ticketStatus === TICKET_STATUS.PROCESSING || ticketStatus === TICKET_STATUS.PREPROCESS
}

export function getStepLabel(stepName) {
  const step = TICKET_CREATION_STEPS.find((step) => step.name === stepName)
  return step?.label || ''
}

// Flat { questionId: answer } map used by draft editing, mutations, and duplication.
// Draft payloads are already flat; submitted snapshots nest the answer under `.answers`.
export function extractAnswers(payload) {
  const answers = {}
  if (!payload || typeof payload !== 'object') return answers
  Object.keys(payload).forEach((key) => {
    if (key === 'sort_order') return
    const entry = payload[key]
    if (entry && typeof entry === 'object' && 'question' in entry) {
      if ('answers' in entry) {
        answers[key] =
          entry.question_type === QUESTION_TYPES.FILE_UPLOAD
            ? normalizeFileUploadAnswer(entry.answers)
            : entry.answers
      }
      return
    }
    answers[key] = isFileUploadAnswer(entry) ? normalizeFileUploadAnswer(entry) : entry
  })
  return answers
}

export function buildQuestionsAnswersSnapshot(payload) {
  if (!payload || typeof payload !== 'object') return []
  const payloadIds = Object.keys(payload).filter((key) => key !== 'sort_order')
  const sortOrder = Array.isArray(payload.sort_order) ? payload.sort_order : []
  const seen = new Set()
  const order = []
  // Follow sort_order first (only ids present in the payload), then append any remaining payload ids
  sortOrder.concat(payloadIds).forEach((questionId) => {
    if (questionId in payload && questionId !== 'sort_order' && !seen.has(questionId)) {
      seen.add(questionId)
      order.push(questionId)
    }
  })
  return order.reduce((items, questionId) => {
    const entry = payload[questionId]
    // Only snapshot entries carry question metadata; flat draft answers yield nothing
    if (!entry || typeof entry !== 'object' || !('question' in entry)) return items
    let answer = entry.question_type === QUESTION_TYPES.FILE_UPLOAD ? [] : null
    if ('answers' in entry) {
      answer =
        entry.question_type === QUESTION_TYPES.FILE_UPLOAD
          ? normalizeFileUploadAnswer(entry.answers)
          : entry.answers
    }
    items.push({
      questionId,
      questionText: entry.question,
      questionType: entry.question_type,
      required: entry.required || false,
      useInAssessment: entry.use_in_assessment || false,
      options: entry.options || null,
      answer,
    })
    return items
  }, [])
}

export function hasQuestionAnswer(answer) {
  if (answer === undefined || answer === null || answer === '') {
    return false
  }

  // file_upload: non-empty file-id list is answered
  if (isFileUploadAnswer(answer)) {
    return normalizeFileUploadAnswer(answer).length > 0
  }

  // Object-format answers (single_select / multiple_select) — empty object is unanswered
  if (typeof answer === 'object' && !Array.isArray(answer)) {
    return Object.keys(answer).length > 0
  }

  return true
}

export function isOptionQuestionType(questionType) {
  return OPTION_QUESTION_TYPES.includes(questionType)
}

/** Resolve display text for a linked question id from live questions, then snapshot. */
export function findQuestionText({ questionId, questions, questionsAnswers }) {
  if (typeof questionId !== 'string' || questionId === '') {
    return ''
  }

  if (Array.isArray(questions)) {
    const live = questions.find((entry) => entry?.questionId === questionId)
    if (typeof live?.questionText === 'string' && live.questionText !== '') {
      return live.questionText
    }
  }

  if (Array.isArray(questionsAnswers)) {
    const snapshot = questionsAnswers.find((entry) => entry?.questionId === questionId)
    if (typeof snapshot?.questionText === 'string' && snapshot.questionText !== '') {
      return snapshot.questionText
    }
  }

  return ''
}

/** Returns answers that belong to current questions and are non-empty. */
export function getMatchingAnswers(answers, questions) {
  if (
    !answers ||
    typeof answers !== 'object' ||
    !Array.isArray(questions) ||
    questions.length === 0
  ) {
    return {}
  }

  const questionIds = questions.map((q) => q.questionId)
  const idLookup = {}
  for (let i = 0; i < questionIds.length; i++) {
    idLookup[questionIds[i]] = true
  }

  const result = {}
  Object.keys(answers).forEach((questionId) => {
    const val = answers[questionId]
    if (idLookup[questionId] && val != null && val !== '' && !isFileUploadAnswer(val)) {
      result[questionId] = val
    }
  })
  return result
}

// Creates a map of questionId -> { ...question, answer }
export function buildQuestionAnswerMap(questions, answers = {}) {
  if (!questions || !Array.isArray(questions)) return {}

  return questions.reduce((map, question) => {
    map[question.questionId] = {
      ...question,
      answer:
        question.questionType === QUESTION_TYPES.FILE_UPLOAD
          ? normalizeFileUploadAnswer(answers[question.questionId])
          : answers[question.questionId] || null,
    }
    return map
  }, {})
}

export function getDuplicateTicketMessages(ticket) {
  if (ticket?.approved === false) {
    return DUPLICATE_TICKET_CONFIG.denied
  }
  return DUPLICATE_TICKET_CONFIG.revoked
}

/**
 * Check if ticket has unsaved changes based on current step
 * @param {Object} baseline - Original ticket data from server
 * @param {Object} current - Current edited ticket data
 * @param {String} step - Current step name
 * @returns {Boolean} - True if there are unsaved changes
 */
export function hasTicketUnsavedChanges(baseline, current, step) {
  switch (step) {
    case 'product': {
      const hasData =
        current.productId ||
        current.companyId ||
        current.companyName ||
        current.productName ||
        current.companyWebsite ||
        current.productWebsite

      if (!baseline) return !!hasData

      if (!hasData) return false

      return (
        baseline.productId !== current.productId ||
        baseline.companyId !== current.companyId ||
        baseline.companyName !== current.companyName ||
        baseline.productName !== current.productName ||
        baseline.companyWebsite !== current.companyWebsite ||
        baseline.productWebsite !== current.productWebsite
      )
    }

    case 'qa': {
      if (!baseline) {
        return false
      }

      return !areObjectsEqual(baseline.answers || {}, current.answers || {})
    }

    case 'complete':
      return false

    default:
      return false
  }
}

export function idForCustomEntry(id, nfInfo) {
  if (id) return id
  if (!nfInfo) return ''
  const hasEntry = Object.values(nfInfo).some((value) => value)
  return hasEntry ? TICKET_CUSTOM_ID_CUSTOM : ''
}

export function resolveCustomWebsites(source) {
  const isCustomCompany = TICKET_CUSTOM_IDS.includes(source?.companyId)
  const isCustomProduct = TICKET_CUSTOM_IDS.includes(source?.productId)

  return {
    isCustomCompany,
    isCustomProduct,
    companyWebsite: isCustomCompany ? source.companyWebsite || '' : source?.companyWebsite || '',
    productWebsite: isCustomProduct ? source.productWebsite || '' : '',
  }
}
